import { Theme } from '@mui/material/styles';
import { Editor as TinyMCEEditor } from 'tinymce';
import makeStyles from '@mui/styles/makeStyles';
import { buildOrganizationWebfontsFromSchema } from '../Fonts/helper';
import { GoogleFontsService } from '../../Services/GoogleFonts/GoogleFontsService';
import { TEXT_CONTENT_MAX_LENGTH } from '../../enums/Constraints';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';

export const DSR_MENTION_SELECTOR = '.dsr-mention';
export const MESSAGE_BASE_CLASSNAME = 'conversation-message';
export const MOUSE_OVER_LISTENER = 'mouseover';
export const MOUSE_LEAVE_LISTENER = 'mouseleave';

export const MENTION_ATTRIBUTES = {
  ENTITY_ID: 'data-entity-id',
  ENTITY_TYPE: 'data-entity-type',
};

// Toolbar types for the Rich Text Editor
export const TEXT_CONTENT_TOOLBAR =
  'bold italic strikethrough link | ' +
  'blocks styleselect | ' +
  'numlist bullist | ' +
  'inserttable | ' +
  'alignleft aligncenter alignright alignjustify | ' +
  'outdent indent | ' +
  'blockquote | ' +
  'emoticons | ' +
  'undo redo | ';

export const ROOM_CONVERSATION_TOOLBAR =
  'bold italic strikethrough | ' +
  'link | ' +
  'numlist bullist | ' +
  'outdent indent | ' +
  'blockquote | ' +
  'mentions | ' +
  'emoticons | ' +
  'undo redo | ';

export const STYLE_SELECT_OPTIONS = [
  { title: 'Paragraph', format: 'p' },
  { title: 'Heading 1', format: 'h1' },
  { title: 'Heading 2', format: 'h2' },
  { title: 'Heading 3', format: 'h3' },
  { title: 'Heading 4', format: 'h4' },
  { title: 'Heading 5', format: 'h5' },
  { title: 'Heading 6', format: 'h6' },
  { title: 'Preformatted', format: 'pre' },
];

export const isValidRTEInput = (editor: TinyMCEEditor | undefined) =>
  !!editor && editor.getContent({ format: 'text' }).trim() !== '';

export const isValidCharacterCount = (editor: TinyMCEEditor | undefined) =>
  !!editor &&
  editor.getContent({ format: 'html' }).length <= TEXT_CONTENT_MAX_LENGTH;

export const inputErrorValidator = (editor: TinyMCEEditor | undefined) => {
  if (!isValidRTEInput(editor)) {
    return 'The text field is required.';
  }

  if (!isValidCharacterCount(editor)) {
    return 'The character limit has been exceeded.';
  }

  return '';
};

/**
 * Builds the CSS rules to overwrite TinyMCE default font and make it match
 * the organization's font. For Enable Us users, it'll default to Inter,
 * but for prospect facing rooms, this should use the font the org configured
 * in Settings > Room Options
 */
export const buildContentStyle = (theme: Theme): string => {
  const { body1 } = theme.typography;

  const org = OrganizationsStore.getCurrentOrganization();

  let fontImport = '';
  let family = body1.fontFamily;
  let weight = body1.fontWeight;

  const orgBodyFont = org?.fonts?.body;

  if (orgBodyFont) {
    const orgFonts = buildOrganizationWebfontsFromSchema({ body: orgBodyFont });
    const fontsImportUrl = new GoogleFontsService().getFontsCSSUrl(orgFonts);

    fontImport = `@import url("${fontsImportUrl}");`;
    family = `"${orgBodyFont.family}", ${family}`;
    weight = orgBodyFont.weight || weight;
  } else {
    /**
     * If there are no org overwrites, then we default to the vendor's font as
     * the default font. Instead of building something dynamic for a hardcoded font,
     * we use the strings from font imports defined at root.css
     *
     * @see https://github.com/referenceable/referenceable_client/blob/develop/src/root.css#L1
     */
    fontImport =
      "@import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap');";
  }

  /**
   * Using HEX codes for colors as TinyMCE doesn't have acces to the root.css
   */

  return `
    ${fontImport}
    body {
      color: ${theme.palette.text.primary};
      font-family: ${family};
      font-size: ${body1.fontSize};
      font-weight: ${weight};
      letter-spacing: ${body1.letterSpacing};
      line-height: ${body1.lineHeight};
    }
    ${DSR_MENTION_SELECTOR} {
      background: #e9f4fe;
      border-radius: ${theme.spacing(0.5)};
      color: ${theme.palette.secondary.main};
      padding: ${theme.spacing(0.25)};
    }
    .mce-content-body ${DSR_MENTION_SELECTOR}[data-mce-selected] {
      outline: 1px solid #b4d7ff;
    }
  `;
};

export const useRichTextEditorStyles = makeStyles((theme: Theme) => ({
  textContainer: {
    borderRadius: theme.spacing(1.25),
    padding: theme.spacing(0),
    '& .tox-tinymce--disabled': {
      pointerEvents: 'none',
    },
    '& .tox-tinymce': {
      border: '2px solid var(--tiny-mce-border-color)',
      borderRadius: theme.spacing(1.25),
    },
    '& .tox:not([dir=rtl]) .tox-toolbar__group:not(:last-of-type)': {
      borderRight: '1px solid transparent',
    },
    '& .tox .tox-toolbar__primary': {
      // values from tinymce
      backgroundPosition: 'center top 39px',
      backgroundRepeat: 'no-repeat',
    },
    '& .tox:not(.tox-tinymce-inline) .tox-editor-header': {
      backgroundColor: 'var(--form-color)',
      borderBottom: 'none',
      boxShadow:
        '0 2px 2px -2px rgba(34,47,62,.1), 0 8px 8px -4px rgba(34,47,62,.07)',
      padding: theme.spacing(0.5, 0),
    },
    '& button:not(.tox-tbtn--bespoke) span.tox-tbtn__select-label': {
      fontSize: theme.spacing(2.75),
      fontWeight: 'bold',
    },
  },
  textContainerError: {
    border: `1px solid ${theme.palette.error.main}`,
  },
}));
